import React, { useMemo, useState } from "react";
import { SortDirection } from "../../../models/sorting";
import { IconAtom } from "../icon";
import styles from "./styles.module.scss";

export interface BaseRow {
  id: string;
  hasChildren?: boolean;
}

export interface Column<T> {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (row: T, parentRow?: T) => React.ReactNode;
  width?: string;
}

interface DataTableProps<T extends BaseRow> {
  columns: Column<T>[];
  data: T[];
  sort?: { key: keyof T; direction: SortDirection };
  onSort?: (key: keyof T, direction: SortDirection) => void;
  loadChildren?: (rowId: string, rowIndex: number) => Promise<T[] | undefined>;
}

export function DataTableAtom<T extends BaseRow>({
  columns,
  data,
  sort,
  onSort,
  loadChildren,
}: Readonly<DataTableProps<T>>) {
  const [expanded, setExpanded] = useState<Record<string, T[]>>({});
  const [loadingRow, setLoadingRow] = useState<string | null>(null);

  useMemo(() => {
    setExpanded({});
  }, [data]);

  const getSortIcon = (colKey: keyof T) => {
    if (sort?.key !== colKey) return "icon-arrow-down-white";
    return sort.direction === "asc" ? "icon-arrow-up-white" : "icon-arrow-down-white";
  };

  const handleSort = (key: keyof T) => {
    if (sort?.key !== key) {
      onSort!(key, "desc");
      return;
    }

    const direction: SortDirection = sort?.key === key && sort.direction === "asc" ? "desc" : "asc";
    return onSort!(key, direction);
  };

  const toggleExpand = async (row: T, rowIndex: number) => {
    if (!row.hasChildren) return;

    if (expanded[row.id]) {
      const copy = { ...expanded };
      delete copy[row.id];
      setExpanded(copy);
      return;
    }

    setLoadingRow(row.id);
    try {
      const children = await loadChildren!(row.id, rowIndex);
      if (children) setExpanded((prev) => ({ ...prev, [row.id]: children }));
    } finally {
      setLoadingRow(null);
    }
  };

  const getRowClass = (rowIndex: number) => (rowIndex % 2 === 0 ? styles.rowEven : styles.rowOdd);

  const isExpandableRow = <T extends BaseRow>(row: T, isChild: boolean) => !!row.hasChildren && !isChild;

  const renderCellContent = <T extends BaseRow>(col: Column<T>, content: React.ReactNode) => {
    if (col.render && col.key !== "actions") {
      return (
        <span className={styles.onderdelenCell}>
          <span className={styles.cellContent}>{content}</span>
        </span>
      );
    }

    return <div className={styles.cellContent}>{content}</div>;
  };

  const rowContent = (row: T, expandable: boolean, isExpanded: boolean, parentRow?: T) => {
    return (
      <>
        {columns.map((col) => {
          const content = col.render ? col.render(row, parentRow) : String(row[col.key as keyof T] ?? "");

          if (col.key === "onderdelen") {
            return (
              <td key="onderdelen" id={col.key} className={styles.rowCell}>
                <span className={styles.onderdelenCell}>
                  <span className={styles.cellContent}>{content}</span>
                  {expandable && (
                    <IconAtom icon={isExpanded ? "icon-dart-top" : "icon-dart-down"} size="small" aria-hidden="true" />
                  )}
                </span>
              </td>
            );
          }

          return (
            <td key={String(col.key)} id={String(col.key)} className={styles.rowCell}>
              {renderCellContent(col, content)}
            </td>
          );
        })}
      </>
    );
  };

  const dropshadowChild = (isFirstChild: boolean, isLastChild: boolean) => {
    return isFirstChild && isLastChild
      ? styles.dropshadowCombined
      : (isFirstChild && styles.dropshadowDown) || (isLastChild && styles.dropshadowUp);
  };

  const renderRow = (row: T, index: number, isChild = false, childIndex = -1, parentRow?: T, childCount = 0) => {
    const baseRowClass = getRowClass(index);
    const isFirstChild = isChild && childIndex === 0;
    const isLastChild = isChild && childIndex === childCount - 1;
    const expandable = isExpandableRow(row, isChild) && loadChildren !== undefined;
    const isExpanded = !!expanded[row.id];
    const dropshadow = dropshadowChild(isFirstChild, isLastChild);

    const onToggle = () => {
      if (!expandable) return;
      toggleExpand(row, index);
    };

    const getRowProps = () => ({
      onClick: onToggle,
      onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) => {
        if (e.key === "Enter" || e.key === " ") onToggle();
      },
      role: expandable ? "button" : undefined,
      tabIndex: expandable ? 0 : undefined,
      "aria-expanded": expandable ? isExpanded : undefined,
      "aria-label": expandable ? "Laat relaties zien" : undefined,
    });

    const rowClass = [baseRowClass, dropshadow, expandable ? styles.rowButton : ""].filter(Boolean).join(" ");

    return (
      <tr key={row.id} className={rowClass} {...getRowProps()}>
        {rowContent(row, expandable, isExpanded, parentRow)}
      </tr>
    );
  };

  const ariaSort = (col: Column<T>) => {
    const direction = sort?.direction === "asc" ? "ascending" : "descending";
    return sort?.key === col.key ? direction : "none";
  };

  return (
    <table className={styles.table}>
      <colgroup>
        {columns.map((col) => (
          <col key={String(col.key) + "-col"} style={col.width ? { width: col.width } : undefined} />
        ))}
      </colgroup>
      <thead>
        <tr>
          {columns.map((col) => (
            <th key={String(col.key)} scope="col" aria-sort={ariaSort(col)}>
              {col.sortable ? (
                <button type="button" className={styles.sortButton} onClick={() => handleSort(col.key as keyof T)}>
                  {col.label}
                  <IconAtom icon={getSortIcon(col.key as keyof T)} size="medium" aria-hidden="true" />
                </button>
              ) : (
                col.label
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((row, index) => (
          <React.Fragment key={row.id}>
            {renderRow(row, index)}

            {loadingRow === row.id && (
              <tr className={styles.dropshadowCombined + " " + getRowClass(index)}>
                <td colSpan={columns.length}>
                  <div className={styles.rowCell}>Laden… </div>
                </td>
              </tr>
            )}

            {expanded[row.id]?.map((child, childIndex, arr) =>
              renderRow(child, index, true, childIndex, row, arr.length)
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
}
